import { actionProps } from "@/lib/controlAnalytics";
import { useQuery } from "@tanstack/react-query";
import Modal from "@/components/common/Modal";
import { CheckIcon } from "@/components/icons";
import { buttonClass } from "@/components/common/controls";
import type { Application } from "@/lib/api/envoy";
import { getTailoredLink } from "@/lib/api/resumeService";
import { shortHash } from "@/lib/applications";
import { formatBytes } from "@/lib/format";
import { useApplicationActions } from "@/lib/hooks/envoy";

function tailoredAt(value: string | null | undefined): string {
    const date = new Date(value ?? "");
    // An instant the service garbled says less, and never breaks the page.
    if (Number.isNaN(date.getTime())) return "tailored";
    const day = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" }).format(date);
    const time = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit" }).format(
        date,
    );
    return `tailored ${day}, ${time}`;
}

/**
 * Mark it sent: names the PDF that went, takes no note, and is recorded
 * once. Sent through the extension this happens by itself.
 */
export default function MarkSentDialog({
    application,
    onClose,
}: {
    application: Application | null;
    onClose: () => void;
}) {
    const { sent } = useApplicationActions();
    const resumeId = application?.resume_id ?? null;
    const link = useQuery({
        queryKey: ["resume", "tailored", resumeId],
        queryFn: () => getTailoredLink(resumeId as string),
        enabled: resumeId !== null,
        staleTime: 60_000,
    });

    return (
        <Modal
            analyticsContext={{
                source: "MarkSentDialog",
                section: "mark_sent",
                application_id: application?.application_id,
            }}
            open={application !== null}
            onClose={onClose}
            title="Mark it sent?"
            description={
                application && (
                    <>
                        <span className="font-semibold text-fg">{application.title}</span> at{" "}
                        {application.company}.{" "}
                        {resumeId
                            ? "The resume below is named as the one that went, and the application leaves the queue."
                            : "It went with your saved resume, and the application leaves the queue."}
                    </>
                )
            }
            footer={
                <>
                    <button
                        {...actionProps("mark_sent_cancelled", {
                            source: "MarkSentDialog",
                            application_id: application?.application_id,
                        })}
                        type="button"
                        onClick={onClose}
                        className={buttonClass("ghost", "lg", "h-[38px] rounded-[11px]")}
                    >
                        Not yet
                    </button>
                    <button
                        {...actionProps("mark_sent_confirm_clicked", {
                            source: "MarkSentDialog",
                            application_id: application?.application_id,
                            resume_id: resumeId ?? undefined,
                        })}
                        type="button"
                        disabled={sent.isPending || !application}
                        onClick={() =>
                            application &&
                            sent.mutate(
                                { applicationId: application.application_id, resumeId },
                                { onSuccess: onClose },
                            )
                        }
                        className={buttonClass("primary", "lg", "h-[38px] gap-1.5 rounded-[11px]")}
                    >
                        <CheckIcon className="size-3.5" strokeWidth={2.6} />
                        Mark sent
                    </button>
                </>
            }
        >
            {resumeId && (
                <div className="flex items-center gap-3 rounded-control border border-green bg-[rgba(33,212,92,.08)] px-3.5 py-3">
                    <span className="grid h-10 w-8 place-items-center rounded-md border border-line bg-card font-mono text-[9px] font-semibold text-fg-muted">
                        PDF
                    </span>
                    <span className="flex flex-1 flex-col gap-px">
                        <span className="font-semibold">Tailored resume</span>
                        <span className="text-[12px] text-fg-muted">
                            {[
                                tailoredAt(application?.ready_at),
                                link.data ? formatBytes(link.data.byte_size) : null,
                                link.data ? shortHash(link.data.sha256) : null,
                            ]
                                .filter(Boolean)
                                .join(" · ")}
                        </span>
                    </span>
                </div>
            )}
            <p className="m-0 text-[11.5px] leading-normal text-fg-faint">
                Recorded once. Doing it again changes nothing. Sent through the extension, this
                happens by itself when the page confirms.
            </p>
        </Modal>
    );
}
